import * as Menu from '@radix-ui/react-dropdown-menu'
import { useNavigate } from 'react-router-dom'
import { useConsumerTradeInsQuery, useVouchersQuery } from '../generated/graphql'
import { bundledDictionaries } from '../localization/dictionaries'
import { type SupportedLocale } from '../localization/types'
import { useTranslation } from '../localization/useTranslation'
import { logout } from '../providers/logout'
import { usePortalContainer } from '../providers/portal'
import { useAuthStore } from '../store/auth'
import { IconCheck, IconGift, IconGlobe, IconInfo, IconLogout, IconMenu } from '../ui/icons'

/**
 * Whether the consumer has anything to show on the "My trade-ins" page — any usable voucher or any
 * past trade-in. Drives the menu entry: greyed out (disabled) when there's nothing, rather than gating
 * on account type. Stays "not empty" while loading so the entry never falsely greys before we know.
 */
function useHasTradeInOverview(): { loading: boolean; hasAny: boolean } {
  const consumerId = useAuthStore((s) => s.consumerId)
  const { data: vData, loading: vLoading } = useVouchersQuery({
    variables: { consumerId: consumerId ?? '' },
    skip: !consumerId,
    fetchPolicy: 'cache-and-network',
  })
  const { data: tData, loading: tLoading } = useConsumerTradeInsQuery({
    skip: !consumerId,
    fetchPolicy: 'cache-and-network',
  })
  const usableVouchers = (vData?.Vouchers ?? []).filter((v) => v.activated && !v.disabled)
  const tradeIns = (tData?.ConsumerTradeIns ?? []).filter(Boolean)
  return { loading: vLoading || tLoading, hasAny: usableVouchers.length > 0 || tradeIns.length > 0 }
}

/**
 * Topbar burger menu (replaces the bare language `<select>`). Built on Radix DropdownMenu, portalled
 * into the shadow-root container and non-modal like our dialogs, so it stays styled and isolated and
 * never restyles the host page. Holds the language switcher (a radio group over the SHOP's languages,
 * hidden when there is only one), a link to "My trade-ins", and log out (clears the session + basket
 * and returns to the login screen — see `logout`).
 *
 * `onHowItWorks` re-opens the first-run intro dialog (owned by FlowLayout) — once-only content with no
 * way back reliably turns into "where did that go".
 */
export function TopbarMenu({ onHowItWorks }: { onHowItWorks: () => void }) {
  const { t, locale, setLocale, availableLocales } = useTranslation()
  const navigate = useNavigate()
  const container = usePortalContainer()
  // Only the shop's own languages are offered (see LocaleProvider.availableLocales). A shop with a
  // single language gets no picker at all — one radio item to choose from is just noise.
  const showLanguages = availableLocales.length > 1
  // The "My trade-ins" entry is always present; it's greyed out when the consumer has nothing to
  // show (no vouchers, no trade-ins) rather than gated on account type. Enabled while still loading.
  const { loading: overviewLoading, hasAny } = useHasTradeInOverview()
  const overviewEmpty = !overviewLoading && !hasAny

  return (
    // modal={false}, like our dialogs: a modal menu scroll-locks the HOST page via react-remove-scroll,
    // whose injected `body[data-scroll-locked]` rule overwrites the body's padding-top. Storefronts that
    // clear an absolutely positioned header with body padding (MOS MOSH) then jump the whole page up
    // under their header the moment the menu opens. Outside-click and Escape still close it.
    <Menu.Root modal={false}>
      <Menu.Trigger className="menu-trigger" aria-label={t('menu.open')}>
        <IconMenu />
      </Menu.Trigger>
      <Menu.Portal container={container ?? undefined}>
        <Menu.Content className="menu" align="end" sideOffset={8}>
          {showLanguages && (
            <>
              <div className="menu-label">
                <IconGlobe />
                {t('menu.language')}
              </div>
              <Menu.RadioGroup
                value={locale}
                onValueChange={(v) => setLocale(v as SupportedLocale)}
              >
                {availableLocales.map((l) => (
                  <Menu.RadioItem key={l} className="menu-item menu-item--radio" value={l}>
                    <span className="menu-check">
                      <Menu.ItemIndicator>
                        <IconCheck />
                      </Menu.ItemIndicator>
                    </span>
                    {bundledDictionaries[l].languageName}
                  </Menu.RadioItem>
                ))}
              </Menu.RadioGroup>

              <Menu.Separator className="menu-sep" />
            </>
          )}

          <Menu.Item
            className="menu-item"
            disabled={overviewEmpty}
            onSelect={() => navigate('/trade-ins')}
          >
            <IconGift />
            {t('menu.myTradeIns')}
            {overviewEmpty && <span className="menu-hint">{t('menu.noneYet')}</span>}
          </Menu.Item>

          <Menu.Item className="menu-item" onSelect={onHowItWorks}>
            <IconInfo />
            {t('menu.howItWorks')}
          </Menu.Item>

          <Menu.Item className="menu-item" onSelect={() => void logout()}>
            <IconLogout />
            {t('menu.logout')}
          </Menu.Item>
        </Menu.Content>
      </Menu.Portal>
    </Menu.Root>
  )
}
